import React, { FC, useContext, useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { ApexLineChart } from '../../../../../PDFViewer/components/ApexChart';
import { AppContext } from '../../../../../../context';
import html2canvas from 'html2canvas';
import styles from './styles';
import {
  SquadPageHeader,
  NavyBar,
  GoldBar,
  KeyValueTable,
  ImagePlaceholder,
  KalenderjahrPreviewTable,
  SquadPage3ImagePreview,
} from './shared';
import { isSquadRasterPie } from '../squadVectorDonut';
import { SquadPortfolioSectionsPreview } from './SquadPortfolioSectionsPreview';
import { squadRasterPieData, type SquadPortfolioSection } from '../squadPortfolioSections';
import {
  captureSquadRollingChart,
  SquadRollingPerformanceChart,
} from '../SquadRollingPerformanceChart';
import {
  captureSquadChart,
  loadSquadChartFont,
  SQUAD_CHART_FONT_STACK,
  useSquadChartFontStatus,
} from '../squadChartFont';
import {
  SQUAD_CHART_CAPTURE_SCALE,
  SQUAD_CHART_STROKE_PX,
  SQUAD_NAV_CHART_HEIGHT_PX,
  SQUAD_NAV_CHART_GRID_PADDING,
  SQUAD_NAV_CHART_Y_LABEL_OFFSET_X,
  SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
  SQUAD_ROLLING_CHART_HEIGHT_PX,
  SQUAD_TABLE_BORDER_BOTTOM,
  SQUAD_TABLE_BORDER_TOP,
  SQUAD_TABLE_BORDER_WIDTH_PX,
  SQUAD_TABLE_FONT_SIZE_PX,
  SQUAD_TABLE_ROW_PAD_Y_PX,
  SQUAD_TABLE_TEXT_COLOR,
} from '../squadPortfolioChartUtils';
import {
  SQUAD_COLORS,
  SQUAD_FONDSDATEN_KEYS,
  SQUAD_WEITERE_DATEN_KEYS,
  SECTION_HEADER_DEFAULTS,
  IMAGE_SLOT_LABELS,
  SQUAD_GEBUEHREN_LABEL_WIDTH_PCT,
} from '../templateConstants';
import { squadInlineTextToHtml } from '../squadInlineText';
import { SQUAD_CHART_AXIS_LABEL_PX, SQUAD_TYPOGRAPHY } from '../pageHeaderTokens';
import { SquadAdvisorsPreview } from '../SquadAdvisorsSection';
import { richHtmlForPreview } from '../squadRichText';
import {
  collectSquadRiskDynamicCells,
  normalizePerformanceRiskTable,
  performanceRiskTableForRender,
} from '../performanceRiskTable';
import { fetchSquadPerformanceRiskValues } from '../fetchSquadPerformanceRisk';
import { fetchSquadGebuehrenValues } from '../fetchSquadGebuehren';
import { resolveSquadGebuehren, SQUAD_DYNAMIC_GEBUEHREN_KEYS } from '../squadGebuehrenDynamic';
import { SquadMifidOverlayLayer } from '../SquadMifidOverlayLayer';
import { normalizeMifidOverlay } from '../squadMifidOverlay';
import { resolveSquadFsHeaderPalette } from '../squadCustomPortfolio';
import { resolveSquadPage3Layout } from '../squadPage3Image';
import {
  expandSquadSectionHeaders,
  resolveSquadHeaderAsOf,
  squadPageMetaLabel,
} from '../sectionHeaderPlaceholders';
import { fetchSquadSectionHeaderDynamicValues } from '../fetchSquadSectionHeadersDynamic';
import {
  fetchSquadSrriwert,
  parseSquadSrriLevel,
  resolveSquadFundIsin,
} from '../squadSrriwert';
import { isSquadDynamicCell, resolveSquadFondsdatenRows } from '../squadFondsdatenDynamic';
import { mapSquadKvRows } from '../squadKvRows';
import { buildSquadFondspartnerBlocks } from '../squadFondspartner';
import { parseSquadFootnotes, squadSidebarFootnoteKey, type SquadSidebarFootnotePage } from '../squadFootnotes';
import { squadLabeledKeys } from '../squadLabels';
import { fetchSquadFondsdatenDynamicValues } from '../fetchSquadFondsdatenDynamic';
import {
  buildSquadNavCategories,
  staffSquadNavFrequency,
  squadNavYRange,
  SquadNavPoint,
  SquadNavYRange,
} from '../squadNavSeries';

export type SectionHeaders = typeof SECTION_HEADER_DEFAULTS;

interface FactsheetPreviewProps {
  data?: any;
  template?: any;
  lineChartLoading?: boolean;
  onChartImageChange?: (dataUrl?: string) => void;
  onChart1ImageChange?: (dataUrl?: string) => void;
  onPieChart1ImageChange?: (dataUrl?: string) => void;
  onPieChart2ImageChange?: (dataUrl?: string) => void;
  onRegisterChartCapture?: (capture: () => Promise<void>) => void;
}

const asKv = (map: Record<string, string> | undefined, keys: Array<{ key: string; label: string }>) =>
  mapSquadKvRows(map, keys);

export const FactsheetPreview: FC<FactsheetPreviewProps> = ({
  data,
  template: templateProp,
  lineChartLoading,
  onChartImageChange,
  onChart1ImageChange,
  onPieChart1ImageChange,
  onPieChart2ImageChange,
  onRegisterChartCapture,
}) => {
  const { document } = useContext(AppContext);
  const pdf = data || {};
  const template = templateProp || document?.template;
  const { primary, secondary } = resolveSquadFsHeaderPalette({
    title: pdf.title || template?.fond,
    farbthemeFund: pdf.farbthemeFund,
    brandPrimary: pdf.brandPrimary || template?.color,
    brandSecondary: pdf.brandSecondary,
  });
  const chartPrimary = SQUAD_COLORS.chartPrimary;
  const portfolioBarColor =
    typeof pdf.portfolioBarColor === 'string' && pdf.portfolioBarColor.trim()
      ? pdf.portfolioBarColor.trim()
      : chartPrimary;
  const portfolioSections: SquadPortfolioSection[] = Array.isArray(pdf.portfolioSections)
    ? pdf.portfolioSections
    : [];
  /** Die ersten beiden Torten (Excel-Reihenfolge) — nur sie werden im Modus „Bild“ aufgenommen. */
  const [rasterPie1, rasterPie2] = squadRasterPieData(portfolioSections);
  const assetPieColors = rasterPie1?.colors || [];
  const mcapPieColors = rasterPie2?.colors || [];
  const [sectionHeaderCache, setSectionHeaderCache] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const isin = resolveSquadFundIsin(pdf, template);
    const until = pdf?.published_at || pdf?.historyUntil;
    (async () => {
      const map = await fetchSquadSectionHeaderDynamicValues(pdf?.sectionHeaders, isin, until);
      if (!cancelled) setSectionHeaderCache(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    pdf?.sectionHeaders,
    pdf?.historyIsin,
    pdf?.fondsdaten?.isin,
    pdf?.published_at,
    pdf?.historyUntil,
    template,
  ]);

  const headers: SectionHeaders = useMemo(
    () =>
      expandSquadSectionHeaders(
        pdf.sectionHeaders,
        resolveSquadHeaderAsOf(pdf),
        sectionHeaderCache,
      ) as SectionHeaders,
    [
      pdf.sectionHeaders,
      pdf.preise?.kursper,
      pdf.data_as_of,
      pdf.historyUntil,
      pdf.published_at,
      sectionHeaderCache,
    ],
  );
  const lineChartDataRaw = pdf.lineChartData || [];
  /** Percent-from-first (website style): starts at 0%, not NAV indexed at ~100. */
  const lineChartData = useMemo(() => {
    if (!Array.isArray(lineChartDataRaw) || lineChartDataRaw.length === 0) return [];
    const firstPoint = lineChartDataRaw[0];
    const firstValue = Array.isArray(firstPoint) ? Number(firstPoint[1]) : Number(firstPoint?.value);
    if (!Number.isFinite(firstValue) || firstValue === 0) return lineChartDataRaw;
    return lineChartDataRaw.map((point: any) => {
      if (Array.isArray(point)) {
        const v = Number(point[1]);
        return [point[0], Number.isFinite(v) ? ((v / firstValue - 1) * 100) : point[1]];
      }
      const v = Number(point?.value);
      return {
        ...point,
        value: Number.isFinite(v) ? ((v / firstValue - 1) * 100) : point?.value,
      };
    });
  }, [lineChartDataRaw]);
  /**
   * Daily below five years, weekly Friday closes above — and an ordinal axis, so
   * weekends and holidays cannot show up as plateaus once the line is thin enough
   * to see them.
   */
  const navSeries = useMemo(() => {
    const raw = lineChartData as any[];
    const points = raw.filter(Array.isArray) as SquadNavPoint[];
    if (points.length === 0 || points.length !== raw.length) {
      return {
        values: [] as number[],
        categories: [] as string[],
        tooltips: [] as string[],
        yRange: null as SquadNavYRange | null,
      };
    }
    const staffed = staffSquadNavFrequency(points);
    const values = staffed.map((point) => Number(point[1]));
    return {
      values,
      yRange: squadNavYRange(values),
      categories: buildSquadNavCategories(staffed),
      tooltips: staffed.map((point) =>
        new Date(Number(point[0])).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
      ),
    };
  }, [lineChartData]);

  const title = pdf.title || template?.fond || 'SQUAD Value - A';

  const [fondsdatenCache, setFondsdatenCache] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const isin = resolveSquadFundIsin(pdf, template);
    const until = pdf?.published_at || pdf?.historyUntil;
    (async () => {
      const map = await fetchSquadFondsdatenDynamicValues(pdf?.fondsdaten, isin, until);
      if (!cancelled) setFondsdatenCache(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    pdf?.fondsdaten,
    pdf?.historyIsin,
    pdf?.fondsdaten?.isin,
    pdf?.published_at,
    pdf?.historyUntil,
    template,
  ]);

  const [riskTableValues, setRiskTableValues] = useState<Map<string, string>>(new Map());
  // Refetch only when the dynamic cells change (source or position), not on every edit of
  // the table's texts; values are keyed by position, so a moved cell must not keep the old.
  const riskTableSignature = useMemo(
    () => JSON.stringify(collectSquadRiskDynamicCells(normalizePerformanceRiskTable(pdf?.performanceRiskTable))),
    [pdf?.performanceRiskTable],
  );
  const [gebuehrenValues, setGebuehrenValues] = useState<Map<string, string>>(new Map());
  // Like the table: refetch when the dynamic Gebühren rows change, not on every keystroke
  // in the typed fees next to them.
  const gebuehrenSignature = useMemo(
    () =>
      JSON.stringify(
        SQUAD_DYNAMIC_GEBUEHREN_KEYS.map((key) => {
          const cell = pdf?.weitereDaten?.[key];
          return isSquadDynamicCell(cell) ? cell : null;
        }),
      ),
    [pdf?.weitereDaten],
  );

  useEffect(() => {
    let cancelled = false;
    const isin = resolveSquadFundIsin(pdf, template);
    const until = pdf?.published_at || pdf?.historyUntil;
    (async () => {
      const map = await fetchSquadGebuehrenValues(pdf?.weitereDaten, isin, until);
      if (!cancelled) setGebuehrenValues(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [gebuehrenSignature, pdf?.historyIsin, pdf?.fondsdaten?.isin, pdf?.published_at, pdf?.historyUntil, template]);

  useEffect(() => {
    let cancelled = false;
    const isin = resolveSquadFundIsin(pdf, template);
    const until = pdf?.published_at || pdf?.historyUntil;
    setRiskTableValues(new Map());
    (async () => {
      const map = await fetchSquadPerformanceRiskValues(pdf?.performanceRiskTable, isin, until);
      if (!cancelled) setRiskTableValues(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [riskTableSignature, pdf?.historyIsin, pdf?.fondsdaten?.isin, pdf?.published_at, pdf?.historyUntil, template]);

  const fondsdatenRows = useMemo(
    () =>
      resolveSquadFondsdatenRows(
        pdf.fondsdaten,
        squadLabeledKeys(SQUAD_FONDSDATEN_KEYS, pdf.fondsdatenLabels),
        fondsdatenCache,
      ),
    [pdf.fondsdaten, pdf.fondsdatenLabels, fondsdatenCache],
  );
  /** Each sidebar page has its own notes, free text as typed — mirrors the PDF. */
  const renderSidebarFootnotes = (page: SquadSidebarFootnotePage) =>
    parseSquadFootnotes(pdf[squadSidebarFootnoteKey(page)]).map((text, i) => (
      <Box key={i} sx={styles.footnote}>
        {text}
      </Box>
    ));
  const partnerBlocks = useMemo(
    () => buildSquadFondspartnerBlocks(pdf.fondspartner),
    [pdf.fondspartner],
  );
  const weitereRows = useMemo(
    () =>
      asKv(
        resolveSquadGebuehren(pdf.weitereDaten, gebuehrenValues),
        squadLabeledKeys(SQUAD_WEITERE_DATEN_KEYS, pdf.weitereDatenLabels),
      ),
    [pdf.weitereDaten, pdf.weitereDatenLabels, gebuehrenValues],
  );

  const perf = useMemo(
    () => performanceRiskTableForRender(pdf.performanceRiskTable, riskTableValues),
    [pdf.performanceRiskTable, riskTableValues],
  );
  const mifidOverlay = useMemo(
    () => normalizeMifidOverlay(pdf.mifidPerformanceOverlay),
    [pdf.mifidPerformanceOverlay],
  );
  const page3Layout = resolveSquadPage3Layout(pdf);
  // No second default here — the mapper sets one. An unreadable level highlights no box.
  const [riskLevel, setRiskLevel] = useState(() => parseSquadSrriLevel(pdf.riskIndicator) ?? 0);
  const chartFontStatus = useSquadChartFontStatus();
  const chartFontReady = chartFontStatus !== 'loading';
  const [rollingHasData, setRollingHasData] = useState(false);
  const rollingHasDataRef = useRef(rollingHasData);
  rollingHasDataRef.current = rollingHasData;

  useEffect(() => {
    let cancelled = false;
    const isin = resolveSquadFundIsin(pdf, template);
    const until = pdf?.published_at || pdf?.historyUntil;
    (async () => {
      const level = await fetchSquadSrriwert(isin, until);
      if (cancelled) return;
      if (level != null) setRiskLevel(level);
      else setRiskLevel(parseSquadSrriLevel(pdf.riskIndicator) ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [pdf?.historyIsin, pdf?.fondsdaten?.isin, pdf?.published_at, pdf?.historyUntil, pdf?.riskIndicator, template]);

  const execChartCapture = useCallback(
    // The chart reaches the PDF as a raster, so its resolution is the only thing
    // keeping the axis labels sharp — scale 3 put them at 322 dpi, visibly soft.
    // Geometry is unaffected: stroke width and label size are set in CSS px and
    // scale with the image, only the pixel density changes.
    // captureSquadChart embeds Segoe UI, which ApexCharts' own export cannot.
    (chartId: string): Promise<string | undefined> =>
      captureSquadChart(chartId, SQUAD_CHART_CAPTURE_SCALE),
    [],
  );

  const execPieCapture = useCallback(async (wrapId: string): Promise<string | undefined> => {
    const el = window.document.getElementById(wrapId);
    if (!el) return undefined;

    el.scrollIntoView({ block: 'nearest' });
    await new Promise((r) => setTimeout(r, 500));

    // Prefer the Chart.js canvas directly — square donut only (legend is native in PDF).
    const chartCanvas = el.querySelector('canvas');
    if (chartCanvas instanceof HTMLCanvasElement && chartCanvas.width > 0) {
      try {
        const dataUrl = chartCanvas.toDataURL('image/png');
        if (dataUrl.length > 5000) return dataUrl;
      } catch {
        // fall through to html2canvas
      }
    }

    try {
      const canvas = await html2canvas(el, {
        scale: SQUAD_CHART_CAPTURE_SCALE,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      if (dataUrl.length > 5000) return dataUrl;
    } catch {
      // ignore
    }
    return undefined;
  }, []);

  const assetAllocation = rasterPie1?.items || [];
  const marktkapitalisierung = rasterPie2?.items || [];
  /** Vector donuts are drawn by the PDF itself — only the raster fallback needs a capture. */
  const rasterPies = isSquadRasterPie(pdf.pieChartStyle);
  const assetAllocationKey = useMemo(() => JSON.stringify(assetAllocation), [assetAllocation]);
  const marktkapitalisierungKey = useMemo(() => JSON.stringify(marktkapitalisierung), [marktkapitalisierung]);
  const assetPieColorsKey = useMemo(() => JSON.stringify(assetPieColors), [assetPieColors]);
  const mcapPieColorsKey = useMemo(() => JSON.stringify(mcapPieColors), [mcapPieColors]);

  const lineChartDataRef = useRef(lineChartData);
  lineChartDataRef.current = lineChartData;

  const captureAll = useCallback(async () => {
    // On a cold load the charts mount only once their font has settled; the delay below
    // then covers their first draw.
    await loadSquadChartFont();
    await new Promise((r) => setTimeout(r, 900));
    if (lineChartDataRef.current?.length) {
      onChartImageChange?.(await execChartCapture('squad-nav-chart'));
    }
    if (rollingHasDataRef.current) {
      await new Promise((r) => setTimeout(r, 450));
      onChart1ImageChange?.(await captureSquadRollingChart());
    }
    if (rasterPies && assetAllocation.length) {
      await new Promise((r) => setTimeout(r, 450));
      onPieChart1ImageChange?.(await execPieCapture('squad-asset-donut-wrap'));
    }
    if (rasterPies && marktkapitalisierung.length) {
      await new Promise((r) => setTimeout(r, 450));
      onPieChart2ImageChange?.(await execPieCapture('squad-mcap-donut-wrap'));
    }
  }, [
    rasterPies,
    assetAllocation.length,
    marktkapitalisierung.length,
    execChartCapture,
    execPieCapture,
    onChartImageChange,
    onChart1ImageChange,
    onPieChart1ImageChange,
    onPieChart2ImageChange,
  ]);

  useEffect(() => {
    onRegisterChartCapture?.(captureAll);
  }, [onRegisterChartCapture, captureAll]);

  useEffect(() => {
    if (!onChartImageChange || !chartFontReady || !lineChartData?.length || lineChartLoading) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 800));
      const uri = await execChartCapture('squad-nav-chart');
      if (!cancelled && uri) onChartImageChange(uri);
    })();
    return () => {
      cancelled = true;
    };
  }, [chartFontReady, chartFontStatus, lineChartData, lineChartLoading, onChartImageChange, execChartCapture]);

  useEffect(() => {
    if (!onChart1ImageChange || !chartFontReady || !rollingHasData) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 1200));
      const uri = await captureSquadRollingChart();
      if (!cancelled && uri) onChart1ImageChange(uri);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    chartFontReady,
    chartFontStatus,
    rollingHasData,
    pdf.rollingPerformance,
    pdf.historyIsin,
    pdf.historyUntil,
    primary,
    secondary,
    onChart1ImageChange,
  ]);

  useEffect(() => {
    if (!onPieChart1ImageChange || !rasterPies || !assetAllocation.length) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 900));
      const uri = await execPieCapture('squad-asset-donut-wrap');
      if (!cancelled && uri) onPieChart1ImageChange(uri);
    })();
    return () => {
      cancelled = true;
    };
  }, [rasterPies, assetAllocationKey, assetPieColorsKey, chartPrimary, onPieChart1ImageChange, execPieCapture]);

  useEffect(() => {
    if (!onPieChart2ImageChange || !rasterPies || !marktkapitalisierung.length) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 900));
      const uri = await execPieCapture('squad-mcap-donut-wrap');
      if (!cancelled && uri) onPieChart2ImageChange(uri);
    })();
    return () => {
      cancelled = true;
    };
  }, [rasterPies, marktkapitalisierungKey, mcapPieColorsKey, chartPrimary, onPieChart2ImageChange, execPieCapture]);

  const renderHeader = (page: number) => (
    <SquadPageHeader
      page={page}
      total={4}
      title={title}
      primary={primary}
      secondary={secondary}
      metaLabel={squadPageMetaLabel(pdf)}
    />
  );

  return (
    <Stack spacing={0} sx={{ maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {/* ───────── PAGE 1 ───────── */}
      <Box sx={styles.page}>
        {renderHeader(1)}
        <Box sx={styles.bodyRow}>
          <Box sx={styles.sidebar}>
            <Box id="squad-fondsdaten-section" sx={{ ...styles.section, mb: '8px' }}>
              {fondsdatenRows.length > 0 ? (
                <>
                  <GoldBar textColor={SQUAD_COLORS.navy}>{headers.fondsdaten}</GoldBar>
                  <KeyValueTable rows={fondsdatenRows} color={SQUAD_COLORS.navy} />
                </>
              ) : null}
            </Box>
            <Box id="squad-fondsberater-section" sx={{ ...styles.section, mb: 0 }}>
              <GoldBar textColor={SQUAD_COLORS.navy}>{headers.fondsberater}</GoldBar>
              <Box sx={{ mt: 0.5 }}>
                <SquadAdvisorsPreview
                  advisors={pdf.advisors}
                  primary={SQUAD_COLORS.navy}
                  secondary={secondary}
                  footerLogoUrl={pdf.advisorLogoImageUrl}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 'auto' }}>{renderSidebarFootnotes(1)}</Box>
          </Box>

          <Box sx={styles.mainCol}>
            <Box id="squad-anlagestrategie-section" sx={styles.page1Section}>
              <NavyBar color={primary}>{headers.anlagestrategie}</NavyBar>
              <Box sx={styles.proseText}>{pdf.anlagestrategie}</Box>
            </Box>

            <Box id="squad-wertentwicklung-seit-beratung-section" sx={styles.page1Section}>
              <NavyBar color={primary}>{headers.wertentwicklungSeitBeratung}</NavyBar>
              <SquadMifidOverlayLayer
                active={mifidOverlay.coverNavChart}
                noticeText={mifidOverlay.noticeText}
                minHeight={SQUAD_NAV_CHART_HEIGHT_PX}
              >
                <Box sx={{ ...styles.capturedChart, height: SQUAD_NAV_CHART_HEIGHT_PX, mb: 0.9 }}>
                  {/* Mounted once the chart font is there: ApexCharts measures its labels on the first draw. */}
                  {chartFontReady ? (
                    <ApexLineChart
                      key={chartFontStatus}
                      id="squad-nav-chart"
                      data={navSeries.values}
                      isSolActive
                      color={secondary}
                      loading={!!lineChartLoading}
                      height={SQUAD_NAV_CHART_HEIGHT_PX}
                      /* Market standard for factsheets: linear point-to-point, thin line,
                         no fill — a smoothed thick area reads as a trend line, not a NAV curve. */
                      curve="straight"
                      strokeWidth={SQUAD_CHART_STROKE_PX}
                      chartType="line"
                      /* Without this ApexCharts paints the gradient fill onto the stroke,
                         so the line changes colour along the x-axis. */
                      fillType="solid"
                      solidFillOpacity={1}
                      yAxisMin={navSeries.yRange?.min}
                      yAxisMax={navSeries.yRange?.max}
                      yAxisTickAmount={navSeries.yRange?.tickAmount}
                      yAxisLabelOffsetX={SQUAD_NAV_CHART_Y_LABEL_OFFSET_X}
                      gridPadding={SQUAD_NAV_CHART_GRID_PADDING}
                      /* Axis labels at footnote size (6 pt) and navy once rasterised into the
                         PDF — same as the rolling chart. */
                      axisLabelFontSize={SQUAD_CHART_AXIS_LABEL_PX}
                      axisLabelColor={SQUAD_COLORS.text}
                      xAxisType="category"
                      xAxisCategories={navSeries.categories}
                      xAxisTooltipLabels={navSeries.tooltips}
                      yAxisPrefix=""
                      /* Decimals come from the chosen step: rounding a 2.5 or 0.25 step
                         to whole percent would label a gridline with a value it is not on. */
                      yAxisFormatter={(value) =>
                        `${
                          Number.isFinite(value)
                            ? value.toLocaleString('de-DE', {
                                minimumFractionDigits: navSeries.yRange?.decimals ?? 0,
                                maximumFractionDigits: navSeries.yRange?.decimals ?? 0,
                              })
                            : value
                        }%`
                      }
                      seriesName={pdf.historyLegendLabel || title}
                      showLegend={false}
                      showToolbar={false}
                      fontFamily={SQUAD_CHART_FONT_STACK}
                    />
                  ) : null}
                </Box>
              </SquadMifidOverlayLayer>
              {pdf.chartFootnote ? (
                <Box component="p" sx={{ ...styles.chartFootnote, m: 0 }}>
                  {pdf.chartFootnote}
                </Box>
              ) : null}
            </Box>

            <Box id="squad-wertentwicklung-risiko-section" sx={styles.page1Section}>
              <NavyBar color={primary}>{headers.wertentwicklungRisiko}</NavyBar>
              <SquadMifidOverlayLayer
                active={mifidOverlay.coverPerformanceRiskTable}
                noticeText={mifidOverlay.noticeText}
              >
                <Box
                  className="squad-portfolio-table custom-portfolio-table"
                  lang="de"
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    fontFamily: SQUAD_PORTFOLIO_PIE_FONT_FAMILY,
                    fontSize: SQUAD_TABLE_FONT_SIZE_PX,
                    color: SQUAD_TABLE_TEXT_COLOR,
                  }}
                >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.max(1, (perf.labels || []).length)}, minmax(0, 1fr))`,
                    columnGap: 0,
                    width: '100%',
                    py: `${SQUAD_TABLE_ROW_PAD_Y_PX}px`,
                    borderTop: `${SQUAD_TABLE_BORDER_WIDTH_PX}px solid ${SQUAD_TABLE_BORDER_TOP}`,
                    borderBottom: `${SQUAD_TABLE_BORDER_WIDTH_PX}px solid ${SQUAD_TABLE_BORDER_BOTTOM}`,
                    marginBottom: '1px',
                    boxSizing: 'border-box',
                  }}
                >
                  {(perf.labels || []).map((l: string, i: number) => (
                    <Box
                      key={i}
                      sx={{
                        minWidth: 0,
                        color: SQUAD_TABLE_TEXT_COLOR,
                        fontSize: SQUAD_TABLE_FONT_SIZE_PX,
                        lineHeight: 1.25,
                        fontWeight: 600,
                        textAlign: i === 0 ? 'left' : 'center',
                      }}
                    >
                      {l}
                    </Box>
                  ))}
                </Box>
                {(perf.rows || []).map((row: string[], ri: number) => (
                  <Box
                    key={ri}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.max(1, row.length)}, minmax(0, 1fr))`,
                      width: '100%',
                      py: `${SQUAD_TABLE_ROW_PAD_Y_PX}px`,
                      borderBottom: `${SQUAD_TABLE_BORDER_WIDTH_PX}px solid ${SQUAD_TABLE_BORDER_BOTTOM}`,
                      boxSizing: 'border-box',
                    }}
                  >
                    {row.map((cell, ci) => (
                      <Box
                        key={ci}
                        sx={{
                          minWidth: 0,
                          color: SQUAD_TABLE_TEXT_COLOR,
                          fontSize: SQUAD_TABLE_FONT_SIZE_PX,
                          lineHeight: 1.25,
                          fontWeight: 400,
                          textAlign: ci === 0 ? 'left' : 'center',
                        }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
              </SquadMifidOverlayLayer>
              {pdf.performanceRiskFootnote ? (
                <Box component="p" sx={{ ...styles.chartFootnote, m: 0 }}>
                  {pdf.performanceRiskFootnote}
                </Box>
              ) : null}
            </Box>

            <Box id="squad-monatskommentar-section" sx={{ ...styles.page1Section, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <NavyBar color={primary}>{headers.monatskommentar}</NavyBar>
              <Box
                sx={styles.proseText}
                dangerouslySetInnerHTML={{ __html: squadInlineTextToHtml(pdf.monatskommentar) }}
              />
            </Box>

            <Box sx={{ ...styles.section, mt: 'auto', flexShrink: 0, mb: 0 }}>
              {pdf.ratingsImageUrl ? (
                <ImagePlaceholder label={IMAGE_SLOT_LABELS.ratingsSeals} height={58} src={pdf.ratingsImageUrl} />
              ) : null}
              {pdf.page1FooterNote ? (
                // Box statt Typography: deren Theme-Variante überschreibt die Größe (20 px IBM Plex)
                <Box component="p" sx={{ ...styles.chartFootnote, mx: 0, mb: 0, mt: 5 }}>
                  {pdf.page1FooterNote}
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ───────── PAGE 2 ───────── */}
      <Box sx={styles.page}>
        {renderHeader(2)}
        <Box sx={styles.bodyRow}>
          <Box sx={styles.sidebar}>
            <Box id="squad-weitere-daten-section" sx={styles.section}>
              {weitereRows.length > 0 ? (
                <>
                  <GoldBar textColor={SQUAD_COLORS.navy}>{headers.weitereDaten}</GoldBar>
                  <KeyValueTable rows={weitereRows} color={SQUAD_COLORS.navy} labelWidthPct={SQUAD_GEBUEHREN_LABEL_WIDTH_PCT} />
                </>
              ) : null}
            </Box>
            <Box id="squad-fondspartner-section" sx={styles.section}>
              {partnerBlocks.length > 0 ? (
                <>
                  <GoldBar textColor={SQUAD_COLORS.navy}>{headers.fondspartner}</GoldBar>
                  <Box sx={{ mt: 1.25 }}>
                    {partnerBlocks.map((block, i) => (
                      <Box key={block.key} sx={{ mt: i === 0 ? 0 : 1.25 }}>
                        <Box
                          sx={{
                            fontSize: SQUAD_TYPOGRAPHY.kontakt,
                            lineHeight: 1.4,
                            color: SQUAD_COLORS.navy,
                          }}
                        >
                          {`${block.label}:`}
                        </Box>
                        {block.lines.map((line, j) => (
                          <Box
                            key={j}
                            sx={{
                              fontSize: SQUAD_TYPOGRAPHY.kontakt,
                              lineHeight: 1.4,
                              color: SQUAD_COLORS.navy,
                            }}
                          >
                            {line}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </>
              ) : null}
            </Box>
            <Box id="squad-kontakt-section" sx={styles.section}>
              <GoldBar textColor={SQUAD_COLORS.navy}>{headers.kontakt}</GoldBar>
              <Box sx={{ mt: 1.25 }}>
                {[pdf.kontakt?.company, pdf.kontakt?.address, pdf.kontakt?.phone, pdf.kontakt?.email, pdf.kontakt?.website]
                  .map((line) => String(line ?? '').trim())
                  .filter(Boolean)
                  .map((line, i, arr) => {
                    const isCompany = i === 0 && String(pdf.kontakt?.company ?? '').trim() === line;
                    const isWebsite = i === arr.length - 1 && String(pdf.kontakt?.website ?? '').trim() === line;
                    // Box — not Typography: theme body1 media query overrides sx fontSize (→20px).
                    return (
                      <Box
                        key={`${line}-${i}`}
                        sx={{
                          fontSize: isCompany ? SQUAD_TYPOGRAPHY.kontaktCompany : SQUAD_TYPOGRAPHY.kontakt,
                          fontWeight: isCompany || isWebsite ? 700 : 400,
                          whiteSpace: line.includes('\n') ? 'pre-line' : undefined,
                          lineHeight: 1.4,
                          color: SQUAD_COLORS.navy,
                        }}
                      >
                        {line}
                      </Box>
                    );
                  })}
              </Box>
            </Box>
            {pdf.fondspartnerLogoImageUrl ? (
              <Box
                component="img"
                src={pdf.fondspartnerLogoImageUrl}
                alt=""
                sx={styles.sidebarBottomLogo}
              />
            ) : null}
            <Box sx={{ mt: pdf.fondspartnerLogoImageUrl ? 1 : 'auto' }}>
              {renderSidebarFootnotes(2)}
            </Box>
          </Box>

          <Box sx={styles.mainCol}>
            <SquadPortfolioSectionsPreview
              sections={portfolioSections}
              headers={headers}
              primary={primary}
              barColor={portfolioBarColor}
              chartPrimary={chartPrimary}
              rasterPies={rasterPies}
              pieStyle={pdf.pieChartStyle}
            />
          </Box>
        </Box>
      </Box>

      {/* ───────── PAGE 3 ───────── */}
      <Box sx={styles.page}>
        {renderHeader(3)}
        <Box sx={styles.bodyRow}>
          <Box sx={styles.sidebar}>
            <Box id="squad-chancen-section" sx={styles.section}>
              <GoldBar textColor={SQUAD_COLORS.navy}>{headers.chancen}</GoldBar>
              <Box sx={{ mt: 1.25, fontSize: SQUAD_TYPOGRAPHY.kontakt, whiteSpace: 'pre-line', lineHeight: 1.4, color: SQUAD_COLORS.navy }}>
                {String(pdf.chancen || '')
                  .split('\n')
                  .map((line: string) => line.replace(/^\+\s*/, '').trim())
                  .filter(Boolean)
                  .map((line: string, i: number) => (
                    <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.6 }}>
                      <Box sx={{ color: SQUAD_COLORS.navy, fontWeight: 700 }}>+</Box>
                      <Box>{line.replace(/^\+\s*/, '')}</Box>
                    </Box>
                  ))}
              </Box>
            </Box>
            <Box id="squad-risiken-section" sx={styles.section}>
              <GoldBar textColor={SQUAD_COLORS.navy}>{headers.risiken}</GoldBar>
              <Box sx={{ mt: 1.25, fontSize: SQUAD_TYPOGRAPHY.kontakt, whiteSpace: 'pre-line', lineHeight: 1.4, color: SQUAD_COLORS.navy }}>
                {String(pdf.risiken || '')
                  .split('\n')
                  .map((line: string) => line.replace(/^[-–−]\s*/, '').trim())
                  .filter(Boolean)
                  .map((line: string, i: number) => (
                    <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.6 }}>
                      <Box sx={{ color: SQUAD_COLORS.navy, fontWeight: 700 }}>-</Box>
                      <Box>{line.replace(/^[-–−]\s*/, '')}</Box>
                    </Box>
                  ))}
                {pdf.risikenNote ? (
                  <Box sx={{ mt: 1, fontStyle: 'italic', lineHeight: 1.35 }}>{pdf.risikenNote}</Box>
                ) : null}
              </Box>
            </Box>
            <Box id="squad-risiko-indikator-section" sx={styles.section}>
              <GoldBar textColor={SQUAD_COLORS.navy}>{headers.risikoIndikator}</GoldBar>
              <Box sx={{ mt: 1.25, color: SQUAD_COLORS.navy }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: SQUAD_TYPOGRAPHY.small,
                    mb: 0.25,
                  }}
                >
                  <span>Niedrigeres Risiko</span>
                  <span>Höheres Risiko</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 0.5 }}>
                  <Box
                    component="svg"
                    viewBox="0 0 7 9"
                    sx={{ width: 7, height: 9, flexShrink: 0, display: 'block' }}
                    aria-hidden
                  >
                    <polygon points="0,4.5 7,0 7,9" fill={SQUAD_COLORS.navy} />
                  </Box>
                  <Box sx={{ flex: 1, height: '2px', bgcolor: SQUAD_COLORS.navy }} />
                  <Box
                    component="svg"
                    viewBox="0 0 7 9"
                    sx={{ width: 7, height: 9, flexShrink: 0, display: 'block' }}
                    aria-hidden
                  >
                    <polygon points="7,4.5 0,0 0,9" fill={SQUAD_COLORS.navy} />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: SQUAD_TYPOGRAPHY.riskScale,
                    mb: 0.75,
                  }}
                >
                  <span>Potentiell geringerer Ertrag</span>
                  <span>Potentiell höherer Ertrag</span>
                </Box>
                <Box sx={{ display: 'flex', width: '100%', mb: 1 }}>
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <Box key={n} sx={styles.riskBox(n === riskLevel, SQUAD_COLORS.navy, n > 1, primary)}>
                      {n}
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    fontSize: SQUAD_TYPOGRAPHY.riskText,
                    lineHeight: 1.35,
                    color: SQUAD_COLORS.navy,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {pdf.riskIndicatorText}
                </Box>
              </Box>
            </Box>
            <Box sx={{ mt: 'auto' }}>{renderSidebarFootnotes(3)}</Box>
          </Box>

          <Box sx={styles.mainCol}>
            <Box id="squad-kalenderjahr-section" sx={styles.section}>
              <NavyBar color={primary}>{headers.kalenderjahr}</NavyBar>
              <SquadMifidOverlayLayer
                active={mifidOverlay.coverKalenderjahr}
                noticeText={mifidOverlay.noticeText}
              >
                <KalenderjahrPreviewTable data={pdf.kalenderjahrPerformance} />
              </SquadMifidOverlayLayer>
              {pdf.kalenderjahrFootnote ? (
                <Box component="p" sx={{ ...styles.chartFootnote, mx: 0, mb: 0, mt: 4 }}>
                  {pdf.kalenderjahrFootnote}
                </Box>
              ) : null}
            </Box>

            <Box id="squad-rollierend-section" sx={styles.section}>
              <NavyBar color={primary}>{headers.rollierend}</NavyBar>
              <SquadMifidOverlayLayer
                active={mifidOverlay.coverRollingPerformance}
                noticeText={mifidOverlay.noticeText}
                minHeight={SQUAD_ROLLING_CHART_HEIGHT_PX}
              >
                <Box sx={styles.capturedChart}>
                  <SquadRollingPerformanceChart
                    data={pdf.rollingPerformance}
                    historyIsin={pdf.historyIsin || template?.isins?.[0]}
                    publishedAt={pdf.published_at}
                    height={SQUAD_ROLLING_CHART_HEIGHT_PX}
                    primaryColor={primary}
                    secondaryColor={secondary}
                    onHasDataChange={setRollingHasData}
                  />
                </Box>
              </SquadMifidOverlayLayer>
              {pdf.rollingPerformanceFootnote ? (
                <Box component="p" sx={{ ...styles.chartFootnote, mx: 0, mb: 0, mt: 4 }}>
                  {pdf.rollingPerformanceFootnote}
                </Box>
              ) : null}
            </Box>

            {page3Layout.kind === 'legacy' ? (
              <>
                <Box id="squad-historische-asset-allokation-section" sx={styles.section}>
                  <NavyBar color={primary}>{headers.historischeAssetAllokation}</NavyBar>
                  <ImagePlaceholder
                    label={IMAGE_SLOT_LABELS.historischeAssetAllokation}
                    height={150}
                    src={pdf.historischeAssetAllokationImageUrl}
                  />
                </Box>

                <Box id="squad-investmentprozess-section" sx={styles.section}>
                  <NavyBar color={primary}>{headers.investmentprozess}</NavyBar>
                  <ImagePlaceholder label={IMAGE_SLOT_LABELS.investmentprozess} height={160} src={pdf.investmentprozessImageUrl} />
                </Box>

                <Box id="squad-zusammenfassung-section" sx={styles.section}>
                  <NavyBar color={primary}>{headers.zusammenfassung}</NavyBar>
                  <ImagePlaceholder
                    label={IMAGE_SLOT_LABELS.zusammenfassung}
                    height={90}
                    src={pdf.zusammenfassungImageUrl}
                    preserveAspectRatio
                  />
                </Box>
              </>
            ) : (
              <SquadPage3ImagePreview src={page3Layout.imageUrl} label={IMAGE_SLOT_LABELS.page3Image} />
            )}
            {pdf.page3FooterNote ? (
              // Anchored at the foot of the column like page 1 — mirrors the PDF
              <Box sx={{ ...styles.section, mt: 'auto', flexShrink: 0, mb: 0 }}>
                <Box component="p" sx={{ ...styles.chartFootnote, mx: 0, mb: 0, mt: 5 }}>
                  {pdf.page3FooterNote}
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* ───────── PAGE 4 ───────── */}
      <Box sx={styles.page}>
        {renderHeader(4)}
        <Box sx={styles.page4Full}>
          <Box id="squad-erlaeuterungen-section" sx={styles.section}>
            <NavyBar color={primary}>{headers.erlaeuterungen}</NavyBar>
            <Box
              sx={{
                ...styles.bodyText,
                fontSize: SQUAD_TYPOGRAPHY.footnote,
                mb: 2.5,
                '& p': { margin: 0, mb: 1.25, '&:last-child': { mb: 0 } },
                '& s, & strike, & del': { textDecoration: 'line-through' },
                '& a': { color: '#0645AD', textDecoration: 'underline' },
              }}
              dangerouslySetInnerHTML={{ __html: richHtmlForPreview(pdf.erlaeuterungen || '') }}
            />
          </Box>
          <Box id="squad-wichtige-hinweise-section" sx={styles.section}>
            <NavyBar color={primary}>{headers.wichtigeHinweise}</NavyBar>
            <Box
              sx={{
                ...styles.bodyText,
                fontSize: SQUAD_TYPOGRAPHY.footnote,
                '& p': { margin: 0, mb: 1.25, '&:last-child': { mb: 0 } },
                '& s, & strike, & del': { textDecoration: 'line-through' },
                '& a': { color: '#0645AD', textDecoration: 'underline' },
              }}
              dangerouslySetInnerHTML={{ __html: richHtmlForPreview(pdf.wichtigeHinweise || '') }}
            />
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
